import React, {
  useEffect,
  useMemo,
  useCallback,
  useState,
  useContext,
} from 'react'
import Head from 'next/head'
import { useSession } from 'next-auth/react'
import { useUser } from '@clerk/nextjs'
import { useFeature } from '@growthbook/growthbook-react'
import type { GetStaticProps, NextPage, InferGetStaticPropsType } from 'next'

import { useDispatch, useSelect } from 'store/index'
import { setQuery } from 'store/search'

import { setDetailPageUrl } from 'reducers/searchData'
import {
  fetchAlgoliaListingsByIdList,
  setFavoritesPageViewed,
  fetchCollections,
} from 'reducers/favorites'

import { PageHistoryContext } from 'context/PageHistoryContext'

import useAugmentedRouter from 'hooks/useAugmentedRouter'

import Header from 'components/Header/Header'
import Footer from 'components/Footer/Footer'
import FavoritesLoginPrompt from 'components/Favorites/FavoritesLogin/FavoritesLoginPrompt'
import FavoritesGrid from 'components/Favorites/FavoritesPage/Grids/FavoritesGrid'
import NoFavorites from 'components/Favorites/FavoritesPage/NoFavorites'
import ResignedGrid from 'components/Favorites/FavoritesPage/Grids/ResignedGrid'
import SearchBox from 'components/Search/SearchBox'
import ResultGridLoader from 'components/Loader/ResultGridLoader'
import FavoritesLoader from 'components/Loader/FavoritesLoader'

import style from './FavoritesPage.module.scss'

import { searchStateToUrl } from 'utils/Search'
import { fetchFooterData, fetchGlobalOptionsData } from 'utils/staticData'

type FavoritesPageProps = {
  footerData: any
}

export const getStaticProps: GetStaticProps<FavoritesPageProps> = async () => {
  const footerData = await fetchFooterData()
  const globalOptionsData = await fetchGlobalOptionsData()

  return {
    props: {
      footerData: {
        menus: footerData,
        options: globalOptionsData.acf.global_footer_group,
      },
    },
  }
}

const Favorites: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  footerData,
}) => {
  const router = useAugmentedRouter()
  const { on: useClerkAuth } = useFeature('clerk-auth')

  // NextAuth session
  const { data: session, status } = useSession()

  // Clerk authentication
  const { isSignedIn, isLoaded: clerkLoaded } = useUser()

  // Determine auth state based on feature flag
  // When useClerkAuth is undefined (GrowthBook not loaded), check both systems
  // to avoid showing login prompt incorrectly during the loading race condition
  const isAuthenticated =
    useClerkAuth === true
      ? isSignedIn
      : useClerkAuth === false
      ? !!session
      : isSignedIn || !!session // GrowthBook not loaded yet, check both

  // Loading if: GrowthBook not loaded, or the active auth system is still loading
  const loading =
    useClerkAuth === undefined ||
    (useClerkAuth && !clerkLoaded) ||
    (!useClerkAuth && status === 'loading')

  // Redux actions
  const appDispatch = useDispatch()

  const { pageHistory } = useContext(PageHistoryContext)

  // Redux selectors
  const saveAsGuest = useSelect((state) => state.favorites.saveAsGuest)
  const selectedListings = useSelect(
    (state) => state.favorites.selectedListings,
  )
  const selectedListingsData = useSelect(
    (state) => state.favorites.selectedListingsData,
  )
  const resignedListingData = useSelect(
    (state) => state.favorites.resignedListingData,
  )
  const resignedIdList = useSelect((state) => state.favorites.resignedIdList)
  const showFavoriteNotification = useSelect(
    (state) => state.favorites.showFavoriteNotification,
  )
  const currentCollectionId = useSelect(
    (state) => state.favorites.currentCollectionId,
  )
  const algoliaFetchPending = useSelect(
    (state) => state.favorites.algoliaFetchPending,
  )
  const loginComplete = useSelect((state) => state.favorites.loginComplete)
  const collectionsFetchPending = useSelect(
    (state) => state.favorites.collectionsFetchPending,
  )
  const addLoginListingsPending = useSelect(
    (state) => state.favorites.addLoginListingsPending,
  )

  const [shouldRender, setShouldRender] = useState(false)

  // Page is ready to render components
  useEffect(() => setShouldRender(true), [])

  // If saveAsGuest, Fetch newly added saved listings
  useEffect(() => {
    if (!isAuthenticated && !loading && saveAsGuest && shouldRender) {
      const validIdList = selectedListings
        .filter(
          (selectedListing) =>
            !resignedIdList.includes(selectedListing.objectID),
        )
        .map((listing) => listing.objectID)

      // Fetch newly added saved listings
      if (validIdList.length && !algoliaFetchPending) {
        const newListings = validIdList.filter(
          (validId) =>
            selectedListingsData
              .map((selectedListing) => selectedListing.objectID)
              .indexOf(validId) < 0,
        )

        if (newListings.length) {
          appDispatch(fetchAlgoliaListingsByIdList(newListings))
        }
      }
    }
  }, [
    appDispatch,
    algoliaFetchPending,
    resignedIdList,
    selectedListings,
    saveAsGuest,
    selectedListingsData,
    shouldRender,
    isAuthenticated,
    loading,
  ])

  // For Clerk authenticated users, fetch Algolia data for favorites
  // (Clerk favorites only store IDs, need to fetch full listing data from Algolia)
  useEffect(() => {
    if (
      useClerkAuth &&
      isAuthenticated &&
      !loading &&
      shouldRender &&
      selectedListings.length &&
      !algoliaFetchPending
    ) {
      const existingDataIds = selectedListingsData.map((l) => l.objectID)
      const newListingIds = selectedListings
        .filter(
          (listing) =>
            !existingDataIds.includes(listing.objectID) &&
            !resignedIdList.includes(listing.objectID),
        )
        .map((listing) => listing.objectID)

      if (newListingIds.length) {
        appDispatch(fetchAlgoliaListingsByIdList(newListingIds))
      }
    }
  }, [
    useClerkAuth,
    isAuthenticated,
    loading,
    shouldRender,
    selectedListings,
    selectedListingsData,
    resignedIdList,
    algoliaFetchPending,
    appDispatch,
  ])

  // setFavoritesPageViewed (remove nav badge)
  useEffect(() => {
    if (showFavoriteNotification) {
      appDispatch(setFavoritesPageViewed(true))
    }
  }, [appDispatch, showFavoriteNotification])

  // Fetch collections after page reload (re-populate selectedListingsData)
  // Note: Collections require NextAuth session for Salesforce API calls
  useEffect(() => {
    if (
      !useClerkAuth &&
      session &&
      selectedListings.length &&
      !selectedListingsData.length &&
      currentCollectionId &&
      currentCollectionId !== 'noCollection'
    ) {
      appDispatch(fetchCollections(session))
    }
  }, [
    appDispatch,
    useClerkAuth,
    session,
    currentCollectionId,
    selectedListings.length,
    selectedListingsData.length,
  ])

  useEffect(() => {
    // first check if history has updated, then if on first page allow default referrer, otherwise pass previous page to referrer
    if (
      pageHistory[pageHistory.length - 1]?.split('?')[0] ===
      window.location.href.split('?')[0]
    ) {
      if (pageHistory.length === 1) {
        window.analytics.page('Favorites Page')
      } else {
        window.analytics.page('Favorites Page', {
          referrer: pageHistory[pageHistory.length - 2],
        })
      }
    }
  }, [router.query.page, pageHistory])

  const handleQuerySelect = useCallback(
    (query) => {
      const searchState = {
        query: query,
      }
      const searchRoute = searchStateToUrl(searchState)
      appDispatch(setQuery(query))
      appDispatch(setDetailPageUrl(router.pathname))
      router.push('/vacation-rentals/search' + searchRoute)
    },
    [appDispatch, router],
  )

  const renderComponents = useMemo(() => {
    let components: React.ReactElement | HTMLElement = <FavoritesLoader />
    const favoritesComponent = selectedListingsData.length ? (
      <FavoritesGrid favorites={selectedListingsData} />
    ) : (
      <NoFavorites />
    )
    const gridLoader = (
      <ResultGridLoader
        gridLength={selectedListings.length > 4 ? selectedListings.length : 4}
      />
    )

    // No saving choice (not authenticated and not saveAsGuest)
    if (!isAuthenticated && !saveAsGuest) {
      components = (
        <div className={style.noFavoritesWrapper}>
          <FavoritesLoginPrompt isModal={false} />
        </div>
      )
      // saveAsGuest (not authenticated but saving as guest)
    } else if (saveAsGuest && !isAuthenticated) {
      components = favoritesComponent
      if (selectedListings.length && !selectedListingsData.length) {
        components = gridLoader
      }
      // Logged in
    } else if (isAuthenticated) {
      components = favoritesComponent

      // Show loader while data is being fetched
      // For Clerk: only check if we have listings but no Algolia data yet
      // For NextAuth/Salesforce: also check loginComplete and collection fetch states
      const isLoadingData =
        selectedListings.length && !selectedListingsData.length
      const isLoadingSalesforce =
        !useClerkAuth &&
        (!loginComplete || collectionsFetchPending || addLoginListingsPending)

      if (isLoadingData || isLoadingSalesforce) {
        components = gridLoader
      }
    }

    return components
  }, [
    useClerkAuth,
    isAuthenticated,
    saveAsGuest,
    selectedListingsData,
    selectedListings.length,
    loginComplete,
    collectionsFetchPending,
    addLoginListingsPending,
  ])

  return (
    <>
      <Header page="favorites">
        <SearchBox onQuerySelect={handleQuerySelect} />
      </Header>
      <div className={style.favoriteListings}>
        <Head>
          <title>Favorites</title>
          <meta content="noindex" name="robots" />
        </Head>
        <div className={`section ${style.favoritesSection}`}>
          <h1 className={`${style.favoritesHeading}`}>Favorites</h1>
          {shouldRender && !loading ? renderComponents : <FavoritesLoader />}
          {shouldRender && !loading ? (
            !selectedListingsData.length && resignedListingData.length ? (
              <ResignedGrid />
            ) : null
          ) : null}
        </div>
      </div>
      <Footer footerData={footerData} />
    </>
  )
}

export default Favorites
